import { DeckPrint, DeckViewer, Frame } from "../headliner/DeckShell";
import { WhatItIs } from "../headliner/WhatItIs";
import { WHO_ASSETS, WhoBehind } from "../headliner/WhoBehind";
import { TEAM_ASSETS, TeamAndPartners } from "../headliner/TeamAndPartners";
import { AtTheTable, Cover, Plainly, Room, StageScene, WhyYou } from "./QuantumSlides";
import { brandOf, brandRootStyle, Wordmark } from "./brands";
import "../buildpartner/build.css";
import "./quantum.css";

/* Quantum Partnership — søsterdeck til Build (samme skall, papir-tema, mottakerlag). 14 slides for kald utsendelse:
   cover · what it is · who is behind it · who is already at the table (Kevin + Replit) · the stage · the room · quantum, plainly · why X · the idea · the main-stage moment · the Quantum Lab · the day · the position · solve this with us.
   Kjernen er «the case» — ett reelt optimeringsproblem, arbeidet frem med partneren i ukene før og vist på hovedscenen. Ingen «live run»-dramaturgi.
   Kategorien koster — beløpet forhandles individuelt (ingen prisliste). Kevin aldri «signert», Replit «agreed in principle». */

const Who = ({ nr, mottaker }) => <WhoBehind nr={nr} mottaker={mottaker} testid="qd-who" />;

/* 08 The idea — case → work → stage → lab → after */
const FLOW = [
  ["Case", "A real optimisation problem from a Norwegian company — selected together with the partner"],
  ["Work", "Modelled and run with the partner's team in the weeks before the summit"],
  ["Stage", "The company and the partner present the case and the result on the main stage — honestly compared"],
  ["Lab", "Problem clinics all day: 700 people test whether their own problems fit"],
  ["After", "The case study «Solved at Norwegian AI Summit» — film, edits, editorial"],
];

const Idea = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The idea" mottaker={mottaker} className="bd-idea">
    <h2 className="bd-h88" data-testid="qd-idea-h">One real case.<br />Worked through<br />before the day.<br />Shown on stage.</h2>
    <ol className="bd-flow" data-testid="qd-flow">
      {FLOW.map(([t, d], i) => <li key={t}><span className="i">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
    <p className="bd-foot" data-testid="qd-idea-foot">Shown, not promised. The format is designed together with the partner.</p>
  </Frame>
);

/* 09 The main-stage moment — casen på scenen, pluss lederbriefingen */
const Moment = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The main-stage moment" mottaker={mottaker} className="bd-stage">
    <div className="bd-big" data-testid="qd-stage-min">20–30 <span className="u">min</span></div>
    <p className="bd-biglab">The case · main stage</p>
    <div className="bd-stage-r">
      <span className="k">How it works</span>
      <p className="p" data-testid="qd-stage-p">A Norwegian business leader and {mottaker} on stage together. The problem, the approach, the result — and what it means for the company. The room watches quantum move from headline to operations.</p>
      <p className="l">One company · one quantum team · one real case</p>
    </div>
    <p className="bd-foot" data-testid="qd-stage-foot">Produced jointly with {mottaker}. <b>Real case. Real result. Honest comparison.</b> Followed by a 20-minute executive briefing: what quantum can do for your business in 2027 — and what it cannot.</p>
  </Frame>
);

/* 10 The Quantum Lab — bemannet, hands-on, ikke stand */
const LAB = [
  ["Problem clinics", "15-minute sessions: bring a scheduling or routing problem, find out whether it fits"],
  ["The case, up close", "The day's case explained by the people who worked on it"],
  ["Hands-on stations", "Guided access to the partner's cloud systems"],
  ["Experts on the floor", "Staffed by the partner's applications team — not a stand, not a roll-up"],
];

const Lab = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Quantum Lab" mottaker={mottaker} className="bd-lab">
    <h2 className="bd-h88" data-testid="qd-lab-h">{brandOf(mottaker).lab}.</h2>
    <p className="bd-lab-sub"><b>The main stage proves it. The Quantum Lab lets 700 people test it on their own problems.</b> People enter curious. They leave knowing whether they have a quantum problem.</p>
    <div className="hd-exec-grid" data-testid="qd-lab-points">
      {LAB.map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="bd-foot">Part of the AI Experience · open throughout the day</p>
  </Frame>
);

/* 11 The day — casen som øyeblikket kvante blir konkret */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)"], ["11:30", "Leadership programme"], ["13:00", "AI Experience · Quantum Lab"],
  ["14:30", "Quantum at work · main stage", true], ["16:00", "Closing keynote"], ["18:30", "Summit Dinner"],
];

const Day = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day bd-day qd-day">
    <h2 className="hd-h2" data-testid="qd-day-h">One venue. One programme.<br />The case lives inside a premium day.</h2>
    <ol className="hd-timeline bd-timeline" data-testid="qd-timeline">
      {DAG.map(([t, l, hi]) => <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span></li>)}
    </ol>
    <p className="hd-dayfoot">700 decision-makers moving through the same day — with the case as the moment quantum becomes concrete.</p>
  </Frame>
);

/* 12 The position — samme modell som Build Partner (Replit): ett selskap per kategori */
const RIGHTS = [
  ["Main-stage moment", "The case, 14:30 — in front of the whole room"],
  ["The Quantum Lab", "Staffed, hands-on, open all day"],
  ["Category exclusivity", "Quantum computing — the only quantum company in the partner programme; scope agreed in the terms"],
  ["Content system", "Main-stage film · 3–5 short-form edits · the case study «Solved at Norwegian AI Summit» · full session on demand · partner-owned clips"],
];
const BDA = [
  ["Before", "The company and the case are selected together and announced. The work happens in the weeks before."],
  ["During", "The case on the main stage. The executive briefing. The Quantum Lab throughout the day."],
  ["After", "Case study · clips · editorial content — «Solved at Norwegian AI Summit»."],
];

const Position = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="hd-one bd-one">
    <h2 className="hd-one-h" data-testid="qd-one-h">One category.<br />One Quantum Partner.</h2>
    <p className="hd-one-sub" data-testid="qd-one-sub">One moment in the room. A content system after it. The same model as our Build Partner, Replit: one company per category — on the main stage and in the room. The quantum position at Norwegian AI Summit: one company owns the story of quantum at work in Norwegian business.</p>
    <ul className="hd-one-pts bd-one-pts" data-testid="qd-one-points">{RIGHTS.map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}</ul>
    <ol className="bd-bda" data-testid="qd-bda">{BDA.map(([h, p]) => <li key={h}><b>{h}</b><span>{p}</span></li>)}</ol>
    <p className="hd-one-univ" data-testid="qd-one-foot">Exclusive within the quantum computing category<i> · </i>scope and investment agreed in the partnership terms.</p>
  </Frame>
);

/* 13 Solve this with us — vi tar produksjonsbyrden; partneren bidrar presist; investeringen avtales individuelt */
const US = ["Stage production, staffing & logistics", "The audience — 700 curated seats", "The Norwegian company and its case — sourced with the partner", "The physical Quantum Lab · broadcast & content production", "Exclusive Quantum Partner position"];
const YOU = ["One senior speaker and an applications team", "The case work — modelling and run, with the company", "Brand collaboration & co-marketing", "Partnership investment — agreed individually"];

const Ask = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Next step" mottaker={mottaker} className="bd-ask qd-ask">
    <h2 className="bd-h88" data-testid="qd-ask-h">Solve this with us.</h2>
    <div className="bd-ask-body">
      <div className="bd-two" data-testid="qd-ask-cols">
        <div className="col"><span className="k">Norwegian AI Summit produces</span><ul>{US.map((u) => <li key={u}>{u}</li>)}</ul></div>
        <div className="col"><span className="k">{mottaker} provides</span><ul>{YOU.map((y) => <li key={y}>{y}</li>)}</ul></div>
      </div>
      <p className="bd-ask-p" data-testid="qd-ask-p">We would like {mottaker} to own the Quantum category at Norwegian AI Summit 2027.</p>
      <p className="bd-terms" data-testid="qd-ask-next">Next step — a 30-minute working session on the case, the format and commercial terms. The investment is agreed individually, not from a rate card.</p>
      <p className="bd-proof" data-testid="qd-ask-proof"><b>Status, plainly:</b> Grieghallen — offer received · Unite — proposed delivery partner · Cure — proposed brand & media partner · Replit — Build Partner, agreed in principle · Kevin O'Leary — availability confirmed for 13 April 2027, not yet contracted · 700 seats — target, invitation-led.</p>
    </div>
    <TeamAndPartners />
    <p className="bd-x bd-x-ask" data-testid="qd-ask-lockup"><b>Norwegian AI Summit</b><i>×</i><Wordmark name={mottaker} h={30} /></p>
  </Frame>
);

const SLIDES = [Cover, WhatItIs, Who, AtTheTable, StageScene, Room, Plainly, WhyYou, Idea, Moment, Lab, Day, Position, Ask];

const ASSETS = ["/images/grieghallen.webp", "/images/headliner/kevin-cover-clean.webp", "/images/headliner/kevin-src-hi.webp", "/images/what-room-v4.webp", "/images/build-stage.webp", "/images/build-stage-d-wave.webp", ...WHO_ASSETS, ...TEAM_ASSETS];

export function QuantumPrint() {
  return <DeckPrint slides={SLIDES} fallback="D-Wave" theme="paper" venue="none" rootStyle={brandRootStyle} />;
}

export default function QuantumDeck({ mottaker = "D-Wave", publicMode = false }) {
  return <DeckViewer deck="quantum" slides={SLIDES} fallback={mottaker} filePrefix="Norwegian-AI-Summit-Quantum-Partnership" assets={ASSETS} theme="paper" venue="none" categoryField={false} publicMode={publicMode} rootStyle={brandRootStyle} />;
}
